// Native social-login bridge for the Capacitor iOS shell, backed by
// @capgo/capacitor-social-login.
//
// Why this exists: inside WKWebView, Google's web OAuth is blocked
// (disallowed_useragent), so the web Google Identity Services button
// (see googleSignIn.js) can't run in the app. The native SDK does the
// sign-in instead and hands us back the same kind of Google ID token,
// which we POST to the EXISTING backend /auth/google endpoint — no
// backend rewrite, just a different token source on native.
//
// Web is untouched: isNativeApp short-circuits everything here, and the
// plugin is dynamically imported so it never enters the web bundle's
// critical path.

import { isNativeApp, isIOS } from './platform';

// Public OAuth client IDs — safe to ship (the iOS one is already embedded
// in Info.plist + the app's URL scheme; the web one is the same value the
// GIS button uses on the website).
const WEB_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID || '';
const IOS_CLIENT_ID =
  import.meta.env.VITE_GOOGLE_IOS_CLIENT_ID ||
  '438372898603-qng0iocre40266d6876mks0r2msvaiop.apps.googleusercontent.com';

// Sign in with Apple master switch. LIVE on iOS since 2026-07-09: the org account
// is approved, the "Sign in with Apple" capability is on the iOS target
// (App.entitlements com.apple.developer.applesignin), the App ID + .p8 key +
// email-relay domain are configured in the Apple portal, and APPLE_TEAM_ID/KEY_ID/
// PRIVATE_KEY are on Railway. The button, plugin init, backend (POST /auth/apple)
// + token revoke are all wired.
//
// iOS-ONLY, deliberately. On Android the capgo plugin's Apple provider needs a
// web-flow config (Service ID clientId + redirectUrl) we haven't set up — and its
// initialize() REJECTS THE WHOLE CALL when `apple` is passed without them, which
// would take Google sign-in down with it (shared init). Google Play also has no
// equivalent of App Store Guideline 4.8 forcing Apple login. Gating the flag on
// isIOS keeps the `apple` init config and every AppleSignInButton mount off
// Android/web in one place. (Web was already excluded: needs that same Service ID.)
export const APPLE_SIGNIN_ENABLED = isIOS;

let initPromise = null;

// Initialize the plugin once per session (idempotent via the cached promise).
// Returns the SocialLogin module, or null on web.
async function ensureInitialized() {
  if (!isNativeApp) return null;
  if (!initPromise) {
    initPromise = (async () => {
      try {
        const { SocialLogin } = await import('@capgo/capacitor-social-login');
        await SocialLogin.initialize({
          google: {
            webClientId: WEB_CLIENT_ID, // used on Android/Web; ignored on iOS
            iOSClientId: IOS_CLIENT_ID,
            // Deliberately NO iOSServerClientId. Passing one makes GoogleSignIn
            // mint an ID token whose audience is that server (web) client — and
            // Google rejects it with `invalid_audience` unless the iOS client and
            // that web client live in the SAME Cloud project, which ours don't
            // (the website's web client predates the Petzy Production project the
            // iOS client was created in). With no server client, the ID token's
            // audience is the iOS client itself, which the backend already
            // accepts (GOOGLE_IOS_CLIENT_ID in the /auth/google audience list).
            mode: 'online',
          },
          // Apple takes an (empty) config object on iOS; only added once the
          // capability is in place so we never initialize a provider the app
          // isn't entitled to use yet.
          ...(APPLE_SIGNIN_ENABLED ? { apple: {} } : {}),
        });
        return SocialLogin;
      } catch (err) {
        // Don't cache a rejected init: a transient failure (plugin/platform
        // not ready yet) would otherwise poison every later attempt with the
        // same stale rejection. Clearing it lets the next tap retry init.
        initPromise = null;
        throw err;
      }
    })();
  }
  return initPromise;
}

// Drive the native Google sign-in and return the Google ID token (JWT).
// Throws on failure; the caller passes the token to
// AuthContext.loginWithGoogle (the same path the web button uses).
export async function signInWithGoogleNative() {
  const SocialLogin = await ensureInitialized();
  if (!SocialLogin) {
    throw new Error('Autentificarea nativă e disponibilă doar în aplicație.');
  }
  // No custom scopes: the basic online sign-in already returns email + profile
  // inside the ID token, which is all we need. Passing `scopes` makes capgo run
  // Google's *authorization* flow instead, which on Android throws
  // "you cannot use scopes without modifying the main activity" (it needs an
  // onActivityResult hook we don't have / don't need). iOS didn't enforce this,
  // so it worked there — but dropping scopes is correct on both platforms.
  const res = await SocialLogin.login({
    provider: 'google',
    // iOS's capgo plugin REQUIRES an `options` object — without it `login`
    // throws "Missing provider or options". Android works with no options and
    // must NOT get `scopes` (throws "you cannot use scopes without modifying the
    // main activity"), so only iOS gets an empty, scope-less options object.
    ...(isIOS ? { options: {} } : {}),
  });
  const idToken = res?.result?.idToken;
  if (!idToken) {
    throw new Error('Google nu a returnat un token de identitate.');
  }
  return idToken;
}

// Drive the native Sign in with Apple. Returns the Apple identity token (JWT)
// plus the user's name — which Apple ONLY provides on the very first
// authorization, so we forward it for the backend to persist. The caller hands
// these to AuthContext.loginWithApple (POST /auth/apple). Gated by
// APPLE_SIGNIN_ENABLED so it's never invoked before the capability exists.
export async function signInWithAppleNative() {
  const SocialLogin = await ensureInitialized();
  if (!SocialLogin) {
    throw new Error('Autentificarea nativă e disponibilă doar în aplicație.');
  }
  const res = await SocialLogin.login({
    provider: 'apple',
    options: { scopes: ['email', 'name'] },
  });
  const identityToken = res?.result?.idToken;
  if (!identityToken) {
    throw new Error('Apple nu a returnat un token de identitate.');
  }
  const p = res?.result?.profile || {};
  const fullName = [p.givenName, p.familyName].filter(Boolean).join(' ') || null;
  // One-time authorization code — the backend exchanges it for a refresh token
  // to later revoke on account deletion (App Review 5.1.1(v)). With the plugin's
  // default token handling the raw code arrives as `accessToken.token`;
  // `authorizationCode` is the field when the plugin's proper-exchange mode is
  // on. Forward whichever is present (null if neither). VERIFY the exact field
  // on-device at go-live — it's the only piece here that can't be unit-tested.
  const authorizationCode =
    res?.result?.authorizationCode || res?.result?.accessToken?.token || null;
  return { identityToken, fullName, authorizationCode };
}

// Sign the native Google SDK out. The SDK keeps its OWN session, separate from
// the Petzy session — without this, after a Petzy logout the next "Continue
// with Google" silently re-uses the last account (no picker). Called from
// AuthContext.logout so "Deconectare" fully logs out and the account chooser
// reappears (matters for switching accounts + shared devices). Best-effort and
// fire-and-forget: no-op on web, and a not-initialized / already-signed-out
// state is swallowed.
export async function signOutGoogleNative() {
  if (!isNativeApp) return;
  try {
    const SocialLogin = await ensureInitialized();
    if (SocialLogin) await SocialLogin.logout({ provider: 'google' });
  } catch {
    /* not initialized / already signed out — nothing to do */
  }
}
